/**
 * Admin V3 — useAdminMutation hook (defined, not yet consumed)
 *
 * Standardised POST/PATCH/PUT/DELETE wrapper. Auto-generates an
 * X-Idempotency-Key header per call (A1 + M2B precedent), tracks in-flight
 * state, and routes success/error to caller-provided callbacks.
 *
 * Lessons absorbed:
 *   - Explicit per-click idempotency-key format: `<slug>-<ts>` (A1, M2B).
 *   - Per-instance loading flag prevents duplicate clicks.
 *   - onError receives the original error so the caller can map dispatch
 *     reasons to French via shared utilities later.
 *
 * Phase 1: NOT consumed (placeholders make no mutations).
 *
 * API:
 *   const { mutate, loading, error, lastResult } = useAdminMutation(endpoint, method);
 *
 *   await mutate(body, options?)
 *     body:    request body (omit for empty)
 *     options: { idempotencyKey?, onSuccess?(data), onError?(err) }
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

const slugify = (endpoint) =>
  (endpoint || '')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '_')
    .slice(0, 60);

const useAdminMutation = (endpoint, method = 'post') => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const mutate = useCallback(async (body = {}, options = {}) => {
    if (loading) return null; // duplicate-call guard
    setLoading(true);
    setError(null);

    const idemKey = options.idempotencyKey || `${slugify(endpoint)}-${Date.now()}`;
    const headers = { ...getAuthHeaders(), 'X-Idempotency-Key': idemKey };

    try {
      const m = (method || 'post').toLowerCase();
      let r;
      if (m === 'post')        r = await axios.post(`${API_URL}${endpoint}`, body, { headers });
      else if (m === 'patch')  r = await axios.patch(`${API_URL}${endpoint}`, body, { headers });
      else if (m === 'put')    r = await axios.put(`${API_URL}${endpoint}`, body, { headers });
      else if (m === 'delete') r = await axios.delete(`${API_URL}${endpoint}`, { headers, data: body });
      else throw new Error(`Unsupported method: ${method}`);

      setLastResult(r.data);
      if (typeof options.onSuccess === 'function') options.onSuccess(r.data);
      return r.data;
    } catch (err) {
      setError(err);
      if (typeof options.onError === 'function') options.onError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint, method, loading, getAuthHeaders]);

  return { mutate, loading, error, lastResult };
};

export default useAdminMutation;
